import { useEffect, useMemo, useRef, useState } from 'react';
import type { Attachment, Conversation, Message } from '../../shared/types';
import { del, get, post } from '../api';
import { isBackSwipe, isReplySwipe } from '../gestures';
import { useCall } from '../hooks/useCall';
import { useChat } from '../state';
import { isOwnMessage } from '../ownership';
import { forgetRoomOwner, roomOwnerKey } from '../roomOwners';
import { CallPanel } from './CallPanel';
import {
  IconBack,
  IconCheck,
  IconPhone,
  IconPhoneOff,
  IconReply,
  IconSend,
  IconSparkles,
  IconTrash,
  IconVideo,
} from './icons';
import { Avatar, PLATFORM_LABEL, dayLabel, formatBytes, formatClock } from './ui';

function Attachments({ attachments }: { attachments: Attachment[] }) {
  return (
    <div className="attachments">
      {attachments.map((attachment) => {
        if (attachment.kind === 'image') {
          return (
            <a key={attachment.id} href={attachment.url} target="_blank" rel="noreferrer" className="att-image">
              <img src={attachment.url} alt={attachment.name ?? 'image'} loading="lazy" />
            </a>
          );
        }
        if (attachment.kind === 'video') {
          return <video key={attachment.id} className="att-video" src={attachment.url} controls preload="metadata" />;
        }
        if (attachment.kind === 'voice' || attachment.kind === 'audio') {
          return <audio key={attachment.id} className="att-audio" src={attachment.url} controls preload="metadata" />;
        }
        return (
          <a key={attachment.id} className="att-file" href={attachment.url} target="_blank" rel="noreferrer">
            <span className="att-file-name">{attachment.name ?? 'File'}</span>
            <span className="att-file-meta">{formatBytes(attachment.size)}</span>
          </a>
        );
      })}
    </div>
  );
}

function MessageRow({
  message,
  own,
  grouped,
  onReply,
}: {
  message: Message;
  own: boolean;
  grouped: boolean;
  onReply: (message: Message) => void;
}) {
  const start = useRef<{ x: number; y: number } | null>(null);
  return (
    <div
      className={`msg-row ${own ? 'self' : 'other'} ${grouped ? 'grouped' : ''}`}
      onTouchStart={(event) => {
        const touch = event.touches[0];
        start.current = { x: touch.clientX, y: touch.clientY };
      }}
      onTouchEnd={(event) => {
        const origin = start.current;
        start.current = null;
        if (!origin) return;
        const touch = event.changedTouches[0];
        if (isReplySwipe(touch.clientX - origin.x, touch.clientY - origin.y)) onReply(message);
      }}
    >
      {!own ? (
        <div className="msg-avatar">{grouped ? null : <Avatar name={message.author.name} url={message.author.avatar} size={32} />}</div>
      ) : null}
      <div className={`bubble ${message.status === 'failed' ? 'failed' : ''}`}>
        {!own && !grouped ? <div className="bubble-author">{message.author.name || 'Peer'}</div> : null}
        {message.replyPreview ? (
          <div className="reply-quote">
            <span>{message.replyPreview}</span>
          </div>
        ) : null}
        {message.attachments?.length ? <Attachments attachments={message.attachments} /> : null}
        {message.text ? (
          <div className="bubble-text">
            {message.text}
            {message.streaming ? <span className="caret" /> : null}
          </div>
        ) : null}
        <div className="bubble-meta">
          {message.via === 'ai' ? <span className="ai-tag">AI</span> : null}
          <span>{formatClock(message.ts)}</span>
          {message.editedAt ? <span className="edited">edited</span> : null}
          {own && message.status === 'sending' ? <span className="pending" /> : null}
          {own && message.status === 'sent' ? <IconCheck size={12} /> : null}
          {message.status === 'failed' ? <span className="failed-tag">failed</span> : null}
        </div>
        {message.error ? <div className="bubble-error">{message.error}</div> : null}
      </div>
      <button className="reply-btn" onClick={() => onReply(message)} title="Reply" aria-label="Reply">
        <IconReply size={14} />
      </button>
    </div>
  );
}

export function ChatView({ conversation }: { conversation: Conversation }) {
  const messages = useChat((state) => state.messages[conversation.id] ?? EMPTY);
  const me = useChat((state) => state.me);
  const setMessages = useChat((state) => state.setMessages);
  const aiMode = useChat((state) => state.aiMode);
  const toggleAiMode = useChat((state) => state.toggleAiMode);
  const notify = useChat((state) => state.notify);
  const capabilities = useChat((state) => state.capabilities);
  const setActive = useChat((state) => state.setActive);
  const removeConversation = useChat((state) => state.removeConversation);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [showCall, setShowCall] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const owned = conversation.platform === 'mesh' && Boolean(roomOwnerKey(conversation.id));
  const call = useCall(conversation.id);

  useEffect(() => {
    let cancelled = false;
    get<{ messages: Message[] }>(`/api/conversations/${encodeURIComponent(conversation.id)}/messages`)
      .then((result) => {
        if (!cancelled) setMessages(conversation.id, result.messages);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [conversation.id, setMessages]);

  const lastText = messages.length ? messages[messages.length - 1].text : '';
  useEffect(() => {
    const element = listRef.current;
    if (!element) return;
    if (stickRef.current) element.scrollTop = element.scrollHeight;
  }, [messages.length, lastText, conversation.id]);

  const onScroll = () => {
    const element = listRef.current;
    if (!element) return;
    stickRef.current = element.scrollHeight - element.scrollTop - element.clientHeight < 90;
  };

  const grouped = useMemo(() => {
    return messages.map((message, index) => {
      const previous = messages[index - 1];
      return Boolean(
        previous &&
          previous.author.id === message.author.id &&
          message.ts - previous.ts < 5 * 60 * 1000 &&
          !previous.replyPreview,
      );
    });
  }, [messages]);

  const send = async () => {
    const raw = draft.trim();
    if (!raw) return;
    const isCommand = /^\/ai\s+/i.test(raw);
    const text = isCommand ? raw.replace(/^\/ai\s+/i, '').trim() : raw;
    if (!text) return;
    const target = replyTo;
    setDraft('');
    setReplyTo(null);
    stickRef.current = true;
    try {
      await post(`/api/conversations/${encodeURIComponent(conversation.id)}/messages`, {
        text,
        replyToId: target?.id,
        authorId: me.id,
        authorName: me.name || undefined,
      });
    } catch (error) {
      notify((error as Error).message, 'error');
    }
    if (isCommand || aiMode) {
      if (!capabilities?.ai.enabled) {
        notify('Add an AI API key in Settings to use the assistant', 'error');
        return;
      }
      try {
        if (call.connected) await call.ask(text);
        else await post(`/api/conversations/${encodeURIComponent(conversation.id)}/ai`, { prompt: text });
      } catch (error) {
        notify((error as Error).message, 'error');
      }
    }
  };

  const startCall = async (withVideo: boolean) => {
    if (!capabilities?.livekit.enabled) {
      notify('Add LiveKit credentials in Settings to start a call', 'error');
      return;
    }
    setShowCall(true);
    await call.join();
    if (withVideo) await call.toggleCamera();
  };

  const hangUp = async () => {
    await call.leave();
    setShowCall(false);
  };

  const copyInvite = async () => {
    if (!conversation.invite) return;
    try {
      await navigator.clipboard.writeText(conversation.invite);
      notify('Mesh invite copied');
    } catch {
      notify(conversation.invite);
    }
  };

  const deleteRoom = async () => {
    const ownerKey = roomOwnerKey(conversation.id);
    if (!ownerKey) {
      setConfirmDelete(false);
      notify('Only the room owner can delete this room', 'error');
      return;
    }
    setDeleting(true);
    try {
      await del(`/api/mesh/rooms/${encodeURIComponent(conversation.remoteId)}`, {
        headers: { 'x-chatsi-owner-key': ownerKey },
      });
      forgetRoomOwner(conversation.id);
      removeConversation(conversation.id);
      setActive(null);
      notify('Room deleted');
    } catch (error) {
      notify((error as Error).message, 'error');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const subtitle = [
    PLATFORM_LABEL[conversation.platform],
    conversation.kind === 'dm' ? 'direct' : conversation.kind,
    conversation.platform === 'mesh' && conversation.peers ? `${conversation.peers} peers` : null,
    owned ? 'owner' : null,
    conversation.meta?.guild,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section className="chat">
      <header className="chat-head">
        <button className="icon-btn back" onClick={() => setActive(null)} aria-label="Back">
          <IconBack />
        </button>
        <Avatar name={conversation.title} url={conversation.avatar} size={40} />
        <div className="chat-title">
          <strong>{conversation.title}</strong>
          <span>
            {subtitle}
            {conversation.invite ? (
              <button className="invite" onClick={copyInvite} title="Copy invite">
                {conversation.invite.slice(0, 8)}…
              </button>
            ) : null}
          </span>
        </div>
        <div className="chat-actions">
          {owned ? (
            <button className="icon-btn danger" onClick={() => setConfirmDelete(true)} title="Delete room" aria-label="Delete room">
              <IconTrash />
            </button>
          ) : null}
          {call.status === 'connected' ? (
            <button className="icon-btn danger" onClick={hangUp} title="Leave call">
              <IconPhoneOff />
            </button>
          ) : (
            <>
              <button className="icon-btn" onClick={() => void startCall(false)} title="Voice call">
                <IconPhone />
              </button>
              <button className="icon-btn" onClick={() => void startCall(true)} title="Video call">
                <IconVideo />
              </button>
            </>
          )}
        </div>
      </header>

      {showCall || call.status !== 'idle' ? (
        <CallPanel call={call} conversation={conversation} onClose={hangUp} />
      ) : null}

      <div
        className="messages"
        ref={listRef}
        onScroll={onScroll}
        onTouchStart={(event) => {
          const touch = event.touches[0];
          swipeStart.current = { x: touch.clientX, y: touch.clientY };
        }}
        onTouchEnd={(event) => {
          const origin = swipeStart.current;
          swipeStart.current = null;
          if (!origin) return;
          const touch = event.changedTouches[0];
          if (isBackSwipe(touch.clientX - origin.x, touch.clientY - origin.y)) setActive(null);
        }}
      >
        <div className="messages-inner">
          {messages.map((message, index) => {
            const previous = messages[index - 1];
            const showDay = !previous || new Date(previous.ts).toDateString() !== new Date(message.ts).toDateString();
            return (
              <div className="msg-block" key={message.id}>
                {showDay ? <div className="day">{dayLabel(message.ts)}</div> : null}
                <MessageRow
                  message={message}
                  own={isOwnMessage(message, me.id, capabilities?.mesh.peerId)}
                  grouped={grouped[index]}
                  onReply={setReplyTo}
                />
              </div>
            );
          })}
          {!messages.length ? <div className="empty-chat">No messages yet. Say hello.</div> : null}
        </div>
      </div>

      {replyTo ? (
        <div className="reply-bar">
          <IconReply size={16} />
          <div>
            <strong>{replyTo.author.name}</strong>
            <span>{replyTo.text.slice(0, 90)}</span>
          </div>
          <button className="icon-btn" onClick={() => setReplyTo(null)} aria-label="Cancel reply">
            ×
          </button>
        </div>
      ) : null}

      <footer className="composer">
        <button
          className={`ai-toggle ${aiMode ? 'on' : ''}`}
          onClick={toggleAiMode}
          title="AI replies to what you send"
        >
          <IconSparkles size={18} />
          <span>AI</span>
        </button>
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void send();
            }
          }}
          placeholder={aiMode ? 'Message · AI will answer' : 'Message'}
          aria-label="Message"
          title="Enter to send · Shift+Enter for a new line"
          rows={1}
        />
        <button className="send" onClick={() => void send()} disabled={!draft.trim()} aria-label="Send">
          <IconSend />
        </button>
      </footer>

      {confirmDelete ? (
        <div className="modal-backdrop" onClick={() => (deleting ? null : setConfirmDelete(false))}>
          <div
            className="modal small"
            role="dialog"
            aria-modal="true"
            aria-label="Delete room"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="modal-head">
              <strong>Delete this room?</strong>
            </header>
            <div className="modal-body">
              <p>
                <strong>{conversation.title}</strong> and its history will be removed from this server. Other peers keep
                their own replicated copies.
              </p>
              <p className="hint">Only the browser holding this room's owner key can delete it. This cannot be undone.</p>
            </div>
            <footer className="modal-foot">
              <button className="btn danger" onClick={() => void deleteRoom()} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete room'}
              </button>
              <button className="btn ghost" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                Cancel
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </section>
  );
}

const EMPTY: Message[] = [];
